import { useState } from "react";
import { api } from "../api.ts";
import { ActorPicker, useActor } from "../components/ActorPicker.tsx";
import { TaskRow } from "../components/TaskRow.tsx";
import { AlertBanner, EmptyState, Segmented } from "../components/ui.tsx";
import { useAsync } from "../hooks.ts";
import type { Me } from "../../shared/types.ts";

type Scope = "everyone" | "kids";

const DAY = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const DATE = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "long" });

/**
 * The front page answers one question: what should we do now.
 *
 * Flat, not nested. Jobs used to sit inside a card per room, which put three
 * levels of chrome around a list of six things; the room is now just a word on
 * the job, and the list reads in one pass.
 */
export function Today({ me }: { me: Me }) {
  const [scope, setScope] = useState<Scope>(me.role === "kid" ? "kids" : "everyone");
  const { data, error, loading, reload } = useAsync(() => api.today(scope === "kids"), [scope]);
  // Only a parent may credit someone else; the server pins everyone else to
  // themselves, so offering them the choice would be a lie.
  const people = useAsync(() => (me.role === "parent" ? api.people() : Promise.resolve([])));
  const { actorId, setActorId } = useActor(people.data ?? [], me.personId);

  if (error) return <AlertBanner>Couldn’t load today’s list. {error}</AlertBanner>;
  if (loading && !data) return <p className="muted">Loading…</p>;
  if (!data) return null;

  const { totals, hidden, rooms } = data;
  const now = new Date();
  const jobs = rooms.flatMap((room) => room.tasks.map((task) => ({ task, room: room.name })));

  return (
    <div>
      <header className="head">
        <div className="head__row">
          <div>
            <span className="eyebrow">{DATE.format(now)}</span>
            <h1 className="head__title">{DAY.format(now)}</h1>
          </div>
          <div className="offer">
            <span className="offer__value">{totals.points}</span>
            <span className="eyebrow">points on offer</span>
          </div>
        </div>
        <p className="head__note">
          {data.away ? `✈️ On ${data.tripName ?? "a trip"} — the house is paused, these travel with you. ` : ""}
          {jobs.length === 0
            ? "Nothing is waiting."
            : `${totals.actionable} ${totals.actionable === 1 ? "job" : "jobs"} waiting${
                totals.overdue > 0 ? `, ${totals.overdue} of them overdue and worth more` : ""
              }.`}
        </p>
      </header>

      {me.role === "parent" && (people.data ?? []).length > 0 ? (
        <ActorPicker people={people.data ?? []} actorId={actorId} onChange={setActorId} />
      ) : null}

      {me.role === "parent" ? (
        <div style={{ marginBottom: "var(--space-6)" }}>
          <Segmented<Scope>
            label="Which jobs to show"
            options={[
              { value: "everyone", label: "Everyone" },
              { value: "kids", label: "Kid jobs" },
            ]}
            value={scope}
            onChange={setScope}
          />
        </div>
      ) : null}

      {jobs.length === 0 ? (
        <EmptyState
          icon="🌿"
          title="Nothing due"
          body={
            scope === "kids"
              ? "No kid jobs waiting. Go and play."
              : "The whole house is inside its schedule. Enjoy it while it lasts."
          }
        />
      ) : (
        <ul className="tags">
          {jobs.map(({ task, room }) => (
            // Personal tasks share an id across kids; the person makes it unique.
            <TaskRow
              key={`${task.id}:${task.personId ?? 0}`}
              task={task}
              personId={actorId}
              onChanged={reload}
              showRoom={room}
            />
          ))}
        </ul>
      )}

      {hidden > 0 ? (
        <p className="kiosk__hidden">{hidden} more waiting, held back so the list stays a day rather than a backlog.</p>
      ) : null}
    </div>
  );
}
